import axios, {AxiosInstance, AxiosResponse}                    from 'axios'
import {toCamelCase, toUnderscore}                              from '@/common/caseConverter'
import storageProxy                                             from '@/common/storageProxy'
import {Account, Auth, Credentials, Dictionary, Invoice, Payer} from '@/types'
import {dateFormat, resolve}                                    from '@/common/helpers'

const ERR_LOAD_INVOICES = 'Erro ao carregar cobranças'
const ERR_LOAD_ACCOUNTS = 'Erro ao carregar empresas'

interface Storate {
    token: string
    payer: Payer | null
}

const storage = storageProxy<Storate>(sessionStorage, 'padmoney:cp', {
    token: '',
    payer: null
})

export async function updateToken(res: AxiosResponse) {
    const {ntk: token} = res.headers
    if (token) {
        storage.token = token
    }
    return res
}


function http(): AxiosInstance {
    const instance = axios.create({
        baseURL: process.env.VUE_APP_API_URL,
        validateStatus: (status) => {
            if (status === 401) {
                logout()
                location.href = '/#/login?expired'
            }
            return true
        },
        headers: {
            'Authorization': storage.token,
            'Content-Type': 'application/json'
        },
        transformRequest: [
            (r) => toUnderscore(r),
            (r) => JSON.stringify(r)
        ],
        transformResponse: [
            (r) => r && JSON.parse(r),
            (r) => r && toCamelCase(r)
        ]
    })
    if (storage.token) {
        instance.interceptors.response.use(updateToken)
    }
    return instance
}

async function processResponse<T>(
    response: Promise<AxiosResponse<T>>,
    defaultMessage?: string,
    defaultData?: any
): Promise<T> {
    const {status, data} = await response

    if (status === 401) {
        return defaultData
    }

    if (status !== 200) {
        const err = data as unknown as { error: string }
        throw new Error((err && err.error) || defaultMessage || 'Erro')
    }

    if (!data && defaultData) {
        return defaultData
    }

    return data
}


export async function sendCode(credentials: Credentials): Promise<void> {
    const response = await http().post('/client-portal/code', credentials)

    if (response.status === 404) {
        throw new Error('Cliente não encontrado.')
    }

    await processResponse(Promise.resolve(response))
}

export async function auth(credentials: Credentials): Promise<Payer> {
    if (!credentials.code) {
        throw new Error('Código inválido.')
    }
    const {token, payer} = await processResponse(http().post<Auth>('/client-portal/auth', credentials))

    storage.token = token
    storage.payer = payer

    return payer
}

export function accounts(): Promise<Account[]> {
    return processResponse(http().get('/client-portal/accounts'), ERR_LOAD_ACCOUNTS, [])
}

export function invoices(filters: Dictionary): Promise<Invoice[]> {
    processInvoiceFilters(filters)
    const config = {params: toUnderscore(filters)}
    return processResponse(http().get('/client-portal/invoices', config), ERR_LOAD_INVOICES, [])
}

function processInvoiceFilters(filters: Dictionary) {
    if (filters.from) {
        filters.from = dateFormat(filters.from as Date | string, 'Y-m-d')
    }
    if (filters.to) {
        filters.to = dateFormat(filters.to as Date | string, 'Y-m-d')
    }
}

export function currentPayer(): Payer | null {
    return storage.payer
}

export function logout() {
    storage.token = ''
    storage.payer = null
}
